import type { ExperienceDraft } from '@/lib/experience-draft';
import { draftAsContent } from '@/lib/experience-draft';
import { pageWhere } from '@/lib/experience-labels';
import { foldForSearch, outOfReachSentence } from '@/lib/variable-palette';
import type { PillPaint, VariableSources } from '@/lib/variable-pill';
import { pillOf, variableStandingOf } from '@/lib/variable-pill';
import type {
  DisplayCondition,
  DisplayConditionSources,
  ExperienceBlockDraft,
  ExperiencePageDraft,
  ExperienceVariable,
  NoyauCode,
  ObjectiveStatus,
} from '@matter_extractor/shared';
import {
  EXPERIENCE_BLOCK_CONDITIONS_MAX,
  NOYAU_CODES,
  assertNever,
  displayConditionVariable,
  experienceVariableToken,
  firstChoiceOf,
  isNoyauCode,
  isPersonNameInput,
  noyauOf,
  variableReach,
} from '@matter_extractor/shared';

/**
 * Les conditions d'affichage, telles que l'éditeur de page les MONTRE et les
 * POSE (refonte modulaire §9.2, H67 à H73 ; maquettes `EditeurConditions`,
 * `ConditionsParNature` ; issue #150).
 *
 * The evaluation is not here: it is `isBlockDisplayed` of `shared`, the one
 * function the reader plays at the display and the server at the advance (H72).
 * What is here is everything an operator's screen needs around it, pure, for
 * the reason `variable-palette.ts` gives at its head - four components ask the
 * same questions (the inspector's section, its palette, the stack's pill, the
 * preview's simulation), and the second one to spell an answer would spell it
 * differently:
 *
 * - WHICH blocks may carry a condition (`offersConditions`, H69);
 * - how a condition READS - its pill, its operator (`conditionPillText`);
 * - what the palette OFFERS where the block stands (`conditionPalette`), which
 *   is `variableReach` of `shared` and nothing of this file's (H60, H61);
 * - the gestures on one condition (`toggleNoyau`, `toggleButton`, `lostButtons`);
 * - what the preview SIMULATES (`simulatedVariables`, `simulationSources`, H73);
 * - who else carries « Nom de la personne dans Sessions » (`personNameHolder`,
 *   H75).
 *
 * ## For the lot of the Noyau block (#151)
 *
 * A nature is added to `displayConditionSchema` in `shared`, then HERE in four
 * places and nowhere else: its option in `conditionPalette`, its summary in
 * `conditionSummary`, its row in `simulatedVariables`, its value in
 * `simulationSources`. The section and the preview read these and need no
 * change of their own.
 */

/* ------------------------------------------------------- who may carry one */

/**
 * Whether a block OFFERS the section « Conditions » (§9.2, H69).
 *
 * « La sortie d'une page ne se masque pas » : a Bouton, whichever its action,
 * and a Noyau block in a choice mode are the ways out of a page, and a page
 * whose only way out were masked would leave a person with no way out. The
 * refusal `condition-on-exit` of `shared` holds the same line at the save.
 */
export function offersConditions(block: ExperienceBlockDraft): boolean {
  if (block.type === 'button') return false;
  if (block.type === 'noyau') return block.mode === 'portrait';
  return true;
}

/** « au plus dix » (§9.2): the bound of `displayConditionSchema`, read and never recopied. */
export function canAddCondition(block: ExperienceBlockDraft): boolean {
  return block.conditions.length < EXPERIENCE_BLOCK_CONDITIONS_MAX;
}

/* -------------------------------------------------------------- the words */

/** « est l'un de », « est cochée »… - the operator of each nature, as the mockup writes it (H67). */
export const CONDITION_OPERATOR_LABEL = {
  checkbox: { checked: 'est cochée', unchecked: 'n’est pas cochée' },
  input: { filled: 'est rempli', empty: 'est vide' },
  objective: { succeeded: 'est réussi', 'not-succeeded': 'n’est pas réussi' },
  oneOf: 'est l’un de',
} as const;

/** The six noyaux, in the order of the table (noyau.md §2.1) - what a noyau condition lists. */
export const CONDITION_NOYAUX: readonly NoyauCode[] = NOYAU_CODES;

/** The Boutons of a page that a CHOICE condition may name: those that leave it. */
export function choiceButtons(
  pages: readonly ExperiencePageDraft[],
  pageId: string,
): { readonly id: string; readonly label: string }[] {
  const page = pages.find((candidate) => candidate.id === pageId);
  if (page === undefined) return [];
  /*
    A Bouton « joue le temps suivant » does not leave its page and writes no
    `{{choix}}` - only the press that leaves does - so a condition naming one
    could never hold. It is not offered, and `shared` refuses it at the save.
  */
  return page.temps
    .flatMap((temps) => temps.blocks)
    .flatMap((block) =>
      block.type === 'button' && block.action === 'next-page'
        ? [{ id: block.id, label: block.label }]
        : [],
    );
}

/**
 * What the pill of a condition says after its variable: « SGP, SPG »,
 * « cochée », « Le matin ». The mockup's pill of the stack, « si @noyau · SGP ».
 */
export function conditionSummary(condition: DisplayCondition, draft: ExperienceDraft): string {
  switch (condition.kind) {
    case 'noyau':
      return condition.noyaux.join(', ');
    case 'choice': {
      const labels = new Map(
        choiceButtons(draft.pages, condition.pageId).map((button) => [button.id, button.label]),
      );
      const named = condition.buttonIds
        .map((id) => labels.get(id) ?? '')
        .filter((one) => one !== '');
      return named.length === 0 ? 'bouton inconnu' : named.join(', ');
    }
    case 'checkbox':
      return condition.operator === 'checked' ? 'cochée' : 'pas cochée';
    case 'input':
      return condition.operator === 'filled' ? 'rempli' : 'vide';
    case 'objective':
      return condition.operator === 'succeeded' ? 'réussi' : 'pas réussi';
    default:
      return assertNever(condition, 'condition summary');
  }
}

/** The pill of the variable a condition cites, as every zone paints it (§8.6, D5). */
export function conditionPaint(
  condition: DisplayCondition,
  sources: VariableSources,
  pageIndex: number | null,
): PillPaint {
  return pillOf(
    variableStandingOf(
      sources,
      pageIndex === null ? null : { kind: 'page', pageIndex },
      displayConditionVariable(condition),
    ),
  );
}

/** « si @noyau · SGP » - the pill of the stack, and the mark of the preview (H73). */
export function conditionPillText(condition: DisplayCondition, sources: VariableSources): string {
  const label = conditionPaint(condition, sources, null).label;
  return `si ${label} · ${conditionSummary(condition, sources.draft)}`;
}

/**
 * The ONE mark of a conditional block in the preview (H73): its first condition,
 * and how many more - « si @noyau · SGP », « si @me recontacter · cochée · et 3
 * autres ». The stack spells every condition in its own pill; the mark only has
 * to say that the block is conditional and under what, in the width of a tab.
 */
export function conditionsMarkText(
  block: { readonly conditions: readonly DisplayCondition[] },
  sources: VariableSources,
): string | null {
  const [first, ...more] = block.conditions;
  if (first === undefined) return null;
  const head = conditionPillText(first, sources);
  if (more.length === 0) return head;
  return `${head} · et ${more.length === 1 ? '1 autre' : `${more.length} autres`}`;
}

/* ------------------------------------------------------------ the palette */

/** One line of « Ajouter une condition »: the variable, where it comes from, and what it poses. */
export type ConditionOption = {
  readonly key: string;
  readonly paint: PillPaint;
  /** « premier choix, page 3 », « étape 2 · obligatoire », « page 1, Consentement ». */
  readonly meta: string;
  /** What choosing the line writes into the block's list - the default operator of its nature. */
  readonly condition: DisplayCondition;
  /** The block already carries a condition on this variable: shown, not offered twice. */
  readonly taken: boolean;
};

export type ConditionGroup = {
  readonly title: string;
  readonly options: readonly ConditionOption[];
};

/** The palette as a whole: its groups, and the foot that says what it does not offer (H61). */
export type ConditionPalette = {
  readonly groups: readonly ConditionGroup[];
  readonly footer: readonly string[];
};

export const CONDITION_GROUP_NOYAU = 'Noyau';
export const CONDITION_GROUP_OBJECTIVES = 'Objectifs';
export const CONDITION_GROUP_FIELDS = 'Champs et choix';

/** The first foot line, always: what a condition never cites, whatever the page. */
export const CONDITION_CRITERIA_EXCLUDED =
  'Pas proposés ici : les critères, qui sont des directions et pas des états.';

/** When the protocol's objectives exist and have no status here yet. */
export const CONDITION_OBJECTIVES_OUT_OF_REACH =
  'Les objectifs du protocole ne se conditionnent qu’après la page protocole : ils ne sont pas proposés ici.';

/** Whether the block already cites this variable in one of its conditions. */
function cites(block: ExperienceBlockDraft, variable: ExperienceVariable): boolean {
  return block.conditions.some((condition) => {
    const cited = displayConditionVariable(condition);
    return cited.kind === variable.kind && cited.id === variable.id;
  });
}

/**
 * What « Ajouter une condition » offers to the block at `pageIndex` (§9.2, H60,
 * H61, H67; maquette `ConditionsParNature`, « Ajouter une condition »).
 *
 * The REACH is `variableReach` of `shared`, the very rule the save refuses on:
 * a variable of this page or of a later one is counted in the foot and never
 * offered, an objective only after the protocol page, `@noyau` only after the
 * first choice. A criterion is never offered - it is a direction, not a state
 * (H68) - and the foot says so, since an absence without a sentence would send
 * an operator looking for it.
 *
 * Each option carries the condition it poses, at the default operator of its
 * nature: « est cochée », « est rempli », « est réussi », and for the two
 * « est l'un de » the first value - a list of zero values is refused by the
 * schema, so the operator starts from one and adds.
 */
export function conditionPalette(
  sources: VariableSources,
  pageIndex: number,
  block: ExperienceBlockDraft,
  query: string,
): ConditionPalette {
  const content = draftAsContent(sources.draft);
  const reach = variableReach(content, { kind: 'page', pageIndex });
  const wanted = foldForSearch(query);
  const keep = (options: ConditionOption[]): ConditionOption[] =>
    wanted === ''
      ? options
      : options.filter((option) => foldForSearch(option.paint.label).includes(wanted));
  const place = { kind: 'page', pageIndex } as const;
  const paintOf = (variable: ExperienceVariable): PillPaint =>
    pillOf(variableStandingOf(sources, place, variable));

  const noyau: ConditionOption[] = [];
  const first = firstChoiceOf(content);
  if (reach.noyau && first !== null) {
    const variable = { kind: 'noyau', id: 'noyau' } as const;
    noyau.push({
      key: 'noyau',
      paint: paintOf(variable),
      meta: `premier choix, ${pageWhere(first.pageIndex, null)}`,
      condition: { kind: 'noyau', operator: 'is-one-of', noyaux: [CONDITION_NOYAUX[0] ?? 'SGP'] },
      taken: cites(block, variable),
    });
  }

  const objectives: ConditionOption[] = [];
  const protocol = sources.protocol;
  if (protocol !== null) {
    protocol.content.steps.forEach((step, stepIndex) => {
      for (const objective of step.objectives) {
        const variable = { kind: 'objective', id: objective.id } as const;
        objectives.push({
          key: experienceVariableToken(variable),
          paint: paintOf(variable),
          meta: `étape ${stepIndex + 1} · ${objective.mandatory ? 'obligatoire' : 'optionnel'}`,
          condition: { kind: 'objective', objectiveId: objective.id, operator: 'succeeded' },
          taken: cites(block, variable),
        });
      }
    });
  }

  const fields: ConditionOption[] = [];
  for (const declared of reach.variables) {
    const variable = { kind: declared.kind, id: declared.id } as const;
    const meta = pageWhere(declared.pageIndex, declared.pageName);
    if (declared.kind === 'choice') {
      const [button] = choiceButtons(sources.draft.pages, declared.id);
      if (button === undefined) continue;
      fields.push({
        key: experienceVariableToken(variable),
        paint: paintOf(variable),
        meta,
        condition: {
          kind: 'choice',
          pageId: declared.id,
          operator: 'is-one-of',
          buttonIds: [button.id],
        },
        taken: cites(block, variable),
      });
      continue;
    }
    const field = sources.draft.pages
      .flatMap((page) => page.temps.flatMap((temps) => temps.blocks))
      .find((candidate) => candidate.id === declared.id);
    if (field?.type !== 'checkbox' && field?.type !== 'input') continue;
    fields.push({
      key: experienceVariableToken(variable),
      paint: paintOf(variable),
      meta,
      condition:
        field.type === 'checkbox'
          ? { kind: 'checkbox', blockId: field.id, operator: 'checked' }
          : { kind: 'input', blockId: field.id, operator: 'filled' },
      taken: cites(block, variable),
    });
  }

  const groups = [
    { title: CONDITION_GROUP_NOYAU, options: keep(noyau) },
    { title: CONDITION_GROUP_OBJECTIVES, options: reach.objectives ? keep(objectives) : [] },
    { title: CONDITION_GROUP_FIELDS, options: keep(fields) },
  ].filter((group) => group.options.length > 0);

  const footer = [CONDITION_CRITERIA_EXCLUDED];
  const hidden = outOfReachSentence(reach.outOfReach, reach.servedAt);
  if (hidden !== null) {
    footer.push(`${hidden} Une condition ne cite qu’une variable des pages d’avant.`);
  }
  if (!reach.objectives && objectives.length > 0) footer.push(CONDITION_OBJECTIVES_OUT_OF_REACH);

  return { groups, footer };
}

/* ------------------------------------------------------------ the gestures */

/** A block with its conditions replaced - every edit of the section ends here. */
export function withConditions<Block extends ExperienceBlockDraft>(
  block: Block,
  conditions: readonly DisplayCondition[],
): Block {
  return { ...block, conditions: [...conditions] };
}

/**
 * Tick or untick one noyau of a noyau condition, and never down to NONE: the
 * schema refuses an empty list (`min(1)`), and an autosave refused mid-gesture
 * would lose the page somebody is composing. The last noyau stays; removing
 * the condition is the gesture that says « plus de filtre ».
 */
export function toggleNoyau(
  condition: Extract<DisplayCondition, { kind: 'noyau' }>,
  code: NoyauCode,
): Extract<DisplayCondition, { kind: 'noyau' }> {
  const has = condition.noyaux.includes(code);
  if (has && condition.noyaux.length === 1) return condition;
  const next = has ? condition.noyaux.filter((one) => one !== code) : [...condition.noyaux, code];
  // Kept in the order of the table, so the pill reads « SGP, SPG » and not the order of the clicks.
  return { ...condition, noyaux: CONDITION_NOYAUX.filter((one) => next.includes(one)) };
}

/**
 * Tick or untick one Bouton of a choice condition, in the order of the page,
 * never down to none - the reason `toggleNoyau` gives. A Bouton the page no
 * longer holds is DROPPED at the first gesture: it is a value no press can give
 * any more, and keeping it would be keeping the refusal of the save.
 */
export function toggleButton(
  condition: Extract<DisplayCondition, { kind: 'choice' }>,
  buttonId: string,
  pages: readonly ExperiencePageDraft[],
): Extract<DisplayCondition, { kind: 'choice' }> {
  const buttons = choiceButtons(pages, condition.pageId).map((button) => button.id);
  const live = condition.buttonIds.filter((id) => buttons.includes(id));
  const has = live.includes(buttonId);
  if (has && live.length === 1) return { ...condition, buttonIds: live };
  const next = has ? live.filter((id) => id !== buttonId) : [...live, buttonId];
  return { ...condition, buttonIds: buttons.filter((id) => next.includes(id)) };
}

/** The Boutons a choice condition names that its page no longer holds. */
export function lostButtons(
  condition: Extract<DisplayCondition, { kind: 'choice' }>,
  pages: readonly ExperiencePageDraft[],
): string[] {
  const buttons = choiceButtons(pages, condition.pageId).map((button) => button.id);
  return condition.buttonIds.filter((id) => !buttons.includes(id));
}

/* ----------------------------------------------------------- the simulation */

/**
 * One variable the conditions of a page cite, as the SIMULATION offers to set
 * it (§9.2, H73; maquette `EditeurConditions`, la barre « Simuler »).
 */
export type SimulatedVariable =
  | { readonly kind: 'noyau'; readonly key: string }
  | {
      readonly kind: 'choice';
      readonly key: string;
      readonly pageId: string;
      readonly buttons: readonly { readonly id: string; readonly label: string }[];
    }
  | { readonly kind: 'checkbox'; readonly key: string; readonly blockId: string }
  | { readonly kind: 'input'; readonly key: string; readonly blockId: string }
  | { readonly kind: 'objective'; readonly key: string; readonly objectiveId: string };

/** Whether any block of the page carries a condition - what puts the bar « Simuler » on screen. */
export function pageHasConditions(page: ExperiencePageDraft): boolean {
  return page.temps.some((temps) => temps.blocks.some((block) => block.conditions.length > 0));
}

/**
 * The variables the conditions of THIS page cite, once each, in reading order:
 * what the bar « Simuler » offers to fix, and nothing more (H73).
 */
export function simulatedVariables(
  page: ExperiencePageDraft,
  pages: readonly ExperiencePageDraft[],
): SimulatedVariable[] {
  const seen = new Set<string>();
  const found: SimulatedVariable[] = [];
  for (const block of page.temps.flatMap((temps) => temps.blocks)) {
    for (const condition of block.conditions) {
      const key = experienceVariableToken(displayConditionVariable(condition));
      if (seen.has(key)) continue;
      seen.add(key);
      switch (condition.kind) {
        case 'noyau':
          found.push({ kind: 'noyau', key });
          break;
        case 'choice':
          found.push({
            kind: 'choice',
            key,
            pageId: condition.pageId,
            buttons: choiceButtons(pages, condition.pageId),
          });
          break;
        case 'checkbox':
          found.push({ kind: 'checkbox', key, blockId: condition.blockId });
          break;
        case 'input':
          found.push({ kind: 'input', key, blockId: condition.blockId });
          break;
        case 'objective':
          found.push({ kind: 'objective', key, objectiveId: condition.objectiveId });
          break;
        default:
          assertNever(condition, 'simulated variable');
      }
    }
  }
  return found;
}

/**
 * The values the simulation FIXES, keyed by the variable's token: a noyau code
 * or null, a Bouton's id or null, a tick, « rempli », « réussi ». A value never
 * set reads as the person who did nothing - no tick, nothing typed, no objective
 * réussi (H71) - except the noyau and the choice, which read their FIRST option:
 * the page is simulated for somebody who went through the sequence, and « aucun
 * noyau » is one choice of the list rather than the silent default.
 */
export type Simulation = Readonly<Record<string, string | boolean | null>>;

/** The noyau a simulation reads when nothing was set: the first of the table. */
function simulatedNoyau(simulation: Simulation, key: string): NoyauCode | null {
  const set = simulation[key];
  if (set === undefined) return CONDITION_NOYAUX[0] ?? null;
  return typeof set === 'string' && isNoyauCode(set) ? set : null;
}

/** The Bouton a simulation reads for a choice when nothing was set: the first of the page. */
function simulatedButton(
  simulation: Simulation,
  variable: Extract<SimulatedVariable, { kind: 'choice' }>,
): string | null {
  const set = simulation[variable.key];
  if (set === undefined) return variable.buttons[0]?.id ?? null;
  return typeof set === 'string' ? set : null;
}

/** What one simulated variable holds, for the control that shows it. */
export function simulatedValue(
  simulation: Simulation,
  variable: SimulatedVariable,
): string | boolean | null {
  switch (variable.kind) {
    case 'noyau':
      return simulatedNoyau(simulation, variable.key);
    case 'choice':
      return simulatedButton(simulation, variable);
    case 'checkbox':
    case 'input':
    case 'objective':
      return simulation[variable.key] === true;
    default:
      return assertNever(variable, 'simulated value');
  }
}

/**
 * The simulation turned into what `isBlockDisplayed` reads (H73): the same
 * sources a session hands the reader, fixed by hand. The page is shown « telle
 * qu'une personne la verra » because it is evaluated by the person's function.
 */
export function simulationSources(
  simulation: Simulation,
  variables: readonly SimulatedVariable[],
  pages: readonly ExperiencePageDraft[],
): DisplayConditionSources {
  const values: Record<string, string | boolean> = {};
  const objectives: Record<string, ObjectiveStatus> = {};
  let noyau: NoyauCode | null = null;
  for (const variable of variables) {
    switch (variable.kind) {
      case 'noyau':
        noyau = simulatedNoyau(simulation, variable.key);
        break;
      case 'choice': {
        const id = simulatedButton(simulation, variable);
        const label = variable.buttons.find((button) => button.id === id)?.label;
        if (label !== undefined) values[variable.pageId] = label;
        break;
      }
      case 'checkbox':
        values[variable.blockId] = simulation[variable.key] === true;
        break;
      case 'input':
        values[variable.blockId] = simulation[variable.key] === true ? 'rempli' : '';
        break;
      case 'objective':
        objectives[variable.objectiveId] =
          simulation[variable.key] === true ? 'succeeded' : 'empty';
        break;
      default:
        assertNever(variable, 'simulation sources');
    }
  }
  return { values, noyau, objectives, pages };
}

/** The name of a noyau as the lists of the editor write it: « Souverain Magicien · SGP ». */
export function noyauOptionLabel(code: NoyauCode): string {
  return `${noyauOf(code).name} · ${code}`;
}

/* ------------------------------------------------------- the person's name */

/**
 * Whether an Input may carry « Nom de la personne dans Sessions » (§9.2, H75):
 * an Input on ONE line. A multi-line answer names nobody, so the inspector does
 * not offer it there and clears it when the Input goes multi-line.
 */
export function offersPersonName(block: ExperienceBlockDraft): boolean {
  return block.type === 'input' && block.lines === 'single';
}

/**
 * Whether a block is the one that names the session: the mark of the stack.
 * `isPersonNameInput` of `shared` - the reading `person-name-repeated` counts
 * and the list of Sessions reads (#152) - and no second spelling of it.
 */
export function namesThePerson(block: ExperienceBlockDraft): boolean {
  return isPersonNameInput(block);
}

/**
 * The OTHER Input of the experience that already carries the flag, or null -
 * what the inspector names under its box before the save refuses two
 * (`person-name-repeated`): « Déjà porté par « Votre prénom », page 1 ».
 */
export function personNameHolder(
  draft: ExperienceDraft,
  blockId: string,
): { readonly label: string; readonly where: string } | null {
  for (const [pageIndex, page] of draft.pages.entries()) {
    for (const block of page.temps.flatMap((temps) => temps.blocks)) {
      if (block.id === blockId || !namesThePerson(block)) continue;
      if (block.type !== 'input') continue;
      return { label: block.label, where: pageWhere(pageIndex, page.name) };
    }
  }
  return null;
}
